import AsyncStorage from '@react-native-async-storage/async-storage';
import { isBefore, subMilliseconds } from 'date-fns';
import { OrderPaymentStatus } from '@sg/graphql-schema';

export const isOrderPaymentStatePending = (
  paymentStateStatus: OrderPaymentStatus | undefined,
  paymentStateExpiration: number | undefined,
  paymentStateExpirationTolerance: number,
) => {
  const paymentStateExpirationDate = new Date(Number(paymentStateExpiration));
  const expirationDate = subMilliseconds(
    new Date(),
    paymentStateExpirationTolerance,
  );

  const isPending =
    paymentStateStatus === OrderPaymentStatus.Created ||
    paymentStateStatus === OrderPaymentStatus.Approved;
  const isExpired = isBefore(paymentStateExpirationDate, expirationDate);

  return isPending && !isExpired;
};

export const getOrderPaymentStateConfig = (config?: Record<string, number>) => {
  return {
    paymentStatePollingInterval:
      getNumberOrNull(config?.pollingInterval) || 3000,
    paymentStateExpirationTolerance:
      getNumberOrNull(config?.expirationTolerance) || 1000 * 60 * 2,
    paymentStateDelayedTolerance:
      getNumberOrNull(config?.delayedTolerance) || 12_000,
  };
};

const getNumberOrNull = (value?: string | number) => {
  return Number.isNaN(Number(value)) ? null : Number(value);
};

/**
 * Sets the order id in local storage after payment intent creation.
 * This is needed because the cart might no longer exist if the order was already fulfilled.
 */
export const setLocalStorageOrderId = async (orderId = '') => {
  await AsyncStorage.setItem(OrderPaymentStateKey, orderId);
};

/**
 * Retrieves the order id from local storage.
 * This is needed because the cart might no longer exist if the order was already fulfilled.
 */
export const getLocalStorageOrderId = async () => {
  return AsyncStorage.getItem(OrderPaymentStateKey);
};

/**
 * Clears the order id from local storage.
 * This is triggered after the order payment state has been resolved (fulfilled or failed).
 */
export const clearLocalStorageOrderId = () => {
  AsyncStorage.removeItem(OrderPaymentStateKey);
};

/**
 * Flag order failure as already shown in local storage.
 * When the customer opens the bag, we'll query the order payment state, which includes the error.
 * So this needs to be set when an error occurs because otherwise we'll show it again.
 */
export const setFailureAlreadyShown = async (paymentIntentId = '') => {
  await AsyncStorage.setItem(LastOrderFailureKey, paymentIntentId);
};

/**
 * Checks if the order failure has already been shown for the given payment intent id.
 * Also updates the local storage with the payment intent id so it can be checked against..
 */
export const checkIfFailureWasAlreadyShown = async (paymentIntentId = '') => {
  const lastOrderFailure = await AsyncStorage.getItem(LastOrderFailureKey);

  return lastOrderFailure === paymentIntentId;
};

export const OrderPaymentStateKey = 'order-payment-state';
export const LastOrderFailureKey = 'last-order-failure';
